/**
 * Form Schema LLM Editor Panel
 *
 * Natural language interface for editing form schemas.
 * Generates suggestions from prompts and allows preview before applying.
 *
 * Features:
 * - Natural language input for schema modifications
 * - Suggestion preview with confidence scores
 * - PII/compliance warnings
 * - Undo/redo support
 * - JSON export
 *
 * Mirrors workflow LLM editor pattern (DRR-056).
 */

import React, { useCallback, useMemo, useState } from 'react';
import {
  ActionIcon,
  Alert,
  Badge,
  Box,
  Button,
  Card,
  Collapse,
  Divider,
  Group,
  Loader,
  Paper,
  Progress,
  ScrollArea,
  Stack,
  Text,
  Textarea,
  Timeline,
  Title,
  Tooltip,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
  IconAlertCircle,
  IconAlertTriangle,
  IconArrowBackUp,
  IconArrowForwardUp,
  IconBrain,
  IconCheck,
  IconChevronDown,
  IconChevronRight,
  IconCircleCheck,
  IconClipboard,
  IconCloudUpload,
  IconCode,
  IconDeviceFloppy,
  IconDownload,
  IconHistory,
  IconRobot,
  IconShieldCheck,
  IconSparkles,
  IconTrash,
  IconWand,
  IconX,
} from '@tabler/icons-react';

import {
  useLlmFormSchemaEditor,
  type FormSchemaEditSuggestion,
  type FormSchemaDraftSummary,
  type UseLlmFormSchemaEditorOptions,
} from '../hooks/useLlmFormSchemaEditor';

// ============================================================================
// Types
// ============================================================================

interface FormSchemaLlmPanelProps {
  entityType: string;
  subgraph: string;
  tenantId: string;
  userId: string;
  mode?: 'CREATE' | 'EDIT' | 'APPROVE' | 'FILTER';
  onSchemaChange?: (json: string) => void;
}

// ============================================================================
// Suggestion Card Component
// ============================================================================

interface SuggestionCardProps {
  suggestion: FormSchemaEditSuggestion;
  isSelected: boolean;
  onSelect: () => void;
  onApply: () => void;
  isApplying: boolean;
}

function SuggestionCard({
  suggestion,
  isSelected,
  onSelect,
  onApply,
  isApplying,
}: SuggestionCardProps) {
  const [expanded, setExpanded] = useState(false);

  const confidenceColor = useMemo(() => {
    if (suggestion.confidence >= 0.8) return 'green';
    if (suggestion.confidence >= 0.6) return 'yellow';
    return 'red';
  }, [suggestion.confidence]);

  const operationBadge = useMemo(() => {
    const colors: Record<string, string> = {
      AddField: 'blue',
      ModifyField: 'cyan',
      RemoveField: 'red',
      ReorderFields: 'grape',
      ModifyLayout: 'violet',
      AddFieldGroup: 'indigo',
      ModifyValidation: 'orange',
      ModifyWidget: 'teal',
    };
    return colors[suggestion.operation] || 'gray';
  }, [suggestion.operation]);

  return (
    <Card
      padding="sm"
      radius="md"
      withBorder
      style={{
        cursor: 'pointer',
        borderColor: isSelected ? 'var(--mantine-color-blue-5)' : undefined,
        backgroundColor: isSelected ? 'var(--mantine-color-blue-0)' : undefined,
      }}
      onClick={onSelect}
    >
      <Stack gap="xs">
        {/* Header */}
        <Group justify="space-between" ta="left">
          <Group gap="xs">
            <Badge color={operationBadge} variant="light" size="sm">
              {suggestion.operation}
            </Badge>
            <Badge color={confidenceColor} variant="outline" size="sm">
              {Math.round(suggestion.confidence * 100)}% confident
            </Badge>
          </Group>
          <ActionIcon
            variant="subtle"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              setExpanded(!expanded);
            }}
          >
            {expanded ? <IconChevronDown size={16} /> : <IconChevronRight size={16} />}
          </ActionIcon>
        </Group>

        {/* Explanation */}
        <Text size="sm">{suggestion.explanation}</Text>

        {/* Affected Fields */}
        {suggestion.affectedFields.length > 0 && (
          <Group gap="xs">
            <Text size="xs" c="dimmed">
              Fields:
            </Text>
            {suggestion.affectedFields.map((field) => (
              <Badge key={field} size="xs" variant="dot">
                {field}
              </Badge>
            ))}
          </Group>
        )}

        {/* Warnings */}
        {suggestion.warnings.length > 0 && (
          <Alert
            icon={<IconAlertTriangle size={16} />}
            color="yellow"
            variant="light"
            title="Warnings"
            p="xs"
          >
            <Stack gap={4}>
              {suggestion.warnings.map((warning, idx) => (
                <Text key={idx} size="xs">
                  {warning}
                </Text>
              ))}
            </Stack>
          </Alert>
        )}

        {/* Compliance Impact */}
        {(suggestion.complianceImpact || suggestion.piiClassification) && (
          <Alert
            icon={<IconShieldCheck size={16} />}
            color="orange"
            variant="light"
            p="xs"
          >
            <Group gap="xs">
              {suggestion.complianceImpact && (
                <Badge color="orange" size="xs">
                  Compliance Impact
                </Badge>
              )}
              {suggestion.piiClassification && (
                <Badge color="red" size="xs">
                  PII: {suggestion.piiClassification}
                </Badge>
              )}
              {suggestion.safetyLevelChange && (
                <Badge color="violet" size="xs">
                  Safety: {suggestion.safetyLevelChange}
                </Badge>
              )}
            </Group>
          </Alert>
        )}

        {/* Expanded: JSON Patch */}
        <Collapse in={expanded}>
          <Paper p="xs" bg="gray.0" radius="sm">
            <Text size="xs" ff="monospace" style={{ whiteSpace: 'pre-wrap' }}>
              {suggestion.jsonPatch}
            </Text>
          </Paper>
        </Collapse>

        {/* Actions */}
        {isSelected && (
          <Group justify="flex-end" mt="xs">
            <Button
              size="xs"
              variant="light"
              leftSection={<IconSparkles size={14} />}
              loading={isApplying}
              onClick={(e) => {
                e.stopPropagation();
                onApply();
              }}
            >
              Apply Change
            </Button>
          </Group>
        )}
      </Stack>
    </Card>
  );
}

// ============================================================================
// Draft List Component
// ============================================================================

interface DraftListProps {
  drafts: FormSchemaDraftSummary[];
  loading: boolean;
  onSelect: (draftId: string) => void;
  onRefresh: () => void;
}

function DraftList({ drafts, loading, onSelect, onRefresh }: DraftListProps) {
  if (loading) {
    return (
      <Box ta="center" py="md">
        <Loader size="sm" />
      </Box>
    );
  }

  if (drafts.length === 0) {
    return (
      <Text size="sm" c="dimmed" ta="center" py="md">
        No drafts found. Create a new one to get started.
      </Text>
    );
  }

  return (
    <Stack gap="xs">
      <Group justify="space-between">
        <Text size="sm" fw={500}>
          Recent Drafts
        </Text>
        <ActionIcon variant="subtle" size="sm" onClick={onRefresh}>
          <IconHistory size={14} />
        </ActionIcon>
      </Group>
      {drafts.slice(0, 5).map((draft) => (
        <Card
          key={draft.draftId}
          padding="xs"
          radius="sm"
          withBorder
          style={{ cursor: 'pointer' }}
          onClick={() => onSelect(draft.draftId)}
        >
          <Group justify="space-between">
            <Stack gap={2}>
              <Text size="sm" fw={500}>
                {draft.entityType}
              </Text>
              <Text size="xs" c="dimmed">
                {draft.subgraph} • {draft.editCount} edits
              </Text>
            </Stack>
            <Badge size="xs" variant="light">
              {draft.status}
            </Badge>
          </Group>
        </Card>
      ))}
    </Stack>
  );
}

// ============================================================================
// Main Panel Component
// ============================================================================

export function FormSchemaLlmPanel({
  entityType,
  subgraph,
  tenantId,
  userId,
  mode = 'CREATE',
  onSchemaChange,
}: FormSchemaLlmPanelProps) {
  const [prompt, setPrompt] = useState('');
  const [historyOpened, { toggle: toggleHistory }] = useDisclosure(false);

  const editor = useLlmFormSchemaEditor({
    userId,
    tenantId,
    entityType,
    subgraph,
    mode,
  });

  const {
    state,
    createDraft,
    loadDraft,
    discardDraft,
    persistDraft,
    generateSuggestions,
    applySuggestion,
    selectSuggestion,
    undo,
    redo,
    undoFromServer,
    exportJson,
    canUndo,
    canRedo,
    canUndoFromServer,
    hasUnsavedChanges,
    draftsList,
    draftsLoading,
    refetchDrafts,
  } = editor;

  // Handle generate suggestions
  const handleGenerate = useCallback(async () => {
    if (!prompt.trim()) {
      notifications.show({
        title: 'Empty Prompt',
        message: 'Please enter a description of the change you want to make.',
        color: 'yellow',
      });
      return;
    }

    await generateSuggestions(prompt);
    setPrompt('');
  }, [prompt, generateSuggestions]);

  // Handle apply suggestion
  const handleApply = useCallback(
    async (suggestionId: string) => {
      await applySuggestion(suggestionId);
      notifications.show({
        title: 'Change Applied',
        message: 'The suggestion has been applied to the schema.',
        color: 'green',
        icon: <IconCheck size={16} />,
      });
      onSchemaChange?.(state.currentJson);
    },
    [applySuggestion, onSchemaChange, state.currentJson]
  );

  // Handle export
  const handleExport = useCallback(async () => {
    const result = await exportJson();
    if (result) {
      const blob = new Blob([result.json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = result.filename;
      a.click();
      URL.revokeObjectURL(url);

      notifications.show({
        title: 'Export Complete',
        message: `Schema exported as ${result.filename}`,
        color: 'green',
      });
    }
  }, [exportJson]);

  // Handle persist
  const handlePersist = useCallback(async () => {
    await persistDraft();
    notifications.show({
      title: 'Schema Saved',
      message: 'The form schema has been saved to the repository.',
      color: 'green',
      icon: <IconCloudUpload size={16} />,
    });
  }, [persistDraft]);

  // Handle discard
  const handleDiscard = useCallback(async () => {
    await discardDraft();
    notifications.show({
      title: 'Draft Discarded',
      message: 'The draft has been discarded.',
      color: 'gray',
    });
  }, [discardDraft]);

  return (
    <Stack gap="md" h="100%">
      {/* Header */}
      <Group justify="space-between">
        <Group gap="xs">
          <IconBrain size={20} />
          <Title order={4}>AI Form Editor</Title>
        </Group>
        <Group gap="xs">
          {state.draft && (
            <>
              <Tooltip label="Undo">
                <ActionIcon
                  variant="subtle"
                  disabled={!canUndo && !canUndoFromServer}
                  onClick={canUndo ? undo : undoFromServer}
                >
                  <IconArrowBackUp size={18} />
                </ActionIcon>
              </Tooltip>
              <Tooltip label="Redo">
                <ActionIcon variant="subtle" disabled={!canRedo} onClick={redo}>
                  <IconArrowForwardUp size={18} />
                </ActionIcon>
              </Tooltip>
              <Divider orientation={'vertical' as const} />
              <Tooltip label="Export JSON">
                <ActionIcon variant="subtle" onClick={handleExport}>
                  <IconDownload size={18} />
                </ActionIcon>
              </Tooltip>
              <Tooltip label="Save to Repository">
                <ActionIcon
                  variant="filled"
                  color="blue"
                  onClick={handlePersist}
                  loading={state.isSaving}
                >
                  <IconCloudUpload size={18} />
                </ActionIcon>
              </Tooltip>
            </>
          )}
        </Group>
      </Group>

      {/* Error Display */}
      {state.error && (
        <Alert
          icon={<IconAlertCircle size={16} />}
          color="red"
          title="Error"
          withCloseButton
          onClose={() => {}}
        >
          {state.error}
        </Alert>
      )}

      {/* No Draft State */}
      {!state.draft && (
        <Stack gap="md">
          <Alert icon={<IconRobot size={16} />} color="blue" variant="light">
            <Text size="sm">
              Create a new draft to start editing the form schema for{' '}
              <strong>
                {entityType} ({subgraph})
              </strong>{' '}
              using natural language.
            </Text>
          </Alert>

          <Button
            leftSection={<IconSparkles size={16} />}
            onClick={createDraft}
            loading={state.isGenerating}
          >
            Create New Draft
          </Button>

          <Divider label="or load existing" />

          <DraftList
            drafts={draftsList}
            loading={draftsLoading}
            onSelect={loadDraft}
            onRefresh={refetchDrafts}
          />
        </Stack>
      )}

      {/* Active Draft State */}
      {state.draft && (
        <Stack gap="md" style={{ flex: 1, minHeight: 0 }}>
          {/* Draft Info */}
          <Paper p="xs" bg="blue.0" radius="sm">
            <Group justify="space-between">
              <Group gap="xs">
                <Badge color="blue" variant="light">
                  Draft: {state.draft.draftId.slice(0, 8)}...
                </Badge>
                <Text size="xs" c="dimmed">
                  {state.draft.editHistory.length} edits
                </Text>
                {hasUnsavedChanges && (
                  <Badge color="yellow" size="xs">
                    Unsaved Changes
                  </Badge>
                )}
              </Group>
              <ActionIcon
                variant="subtle"
                color="red"
                size="sm"
                onClick={handleDiscard}
              >
                <IconTrash size={14} />
              </ActionIcon>
            </Group>
          </Paper>

          {/* Prompt Input */}
          <Stack gap="xs">
            <Textarea
              placeholder="Describe the change you want to make... e.g., 'Add a phone number field after email'"
              value={prompt}
              onChange={(e) => setPrompt(e.currentTarget.value)}
              minRows={2}
              maxRows={4}
              autosize
              disabled={state.isGenerating}
              rightSection={
                state.isGenerating ? <Loader size="xs" /> : undefined
              }
            />
            <Group justify="space-between">
              <Button
                variant="subtle"
                size="xs"
                leftSection={<IconHistory size={14} />}
                onClick={toggleHistory}
              >
                Prompt History ({state.promptHistory.length})
              </Button>
              <Button
                leftSection={<IconWand size={16} />}
                onClick={handleGenerate}
                loading={state.isGenerating}
                disabled={!prompt.trim()}
              >
                Generate Suggestions
              </Button>
            </Group>
          </Stack>

          {/* Prompt History */}
          <Collapse in={historyOpened}>
            <Paper p="xs" bg="gray.0" radius="sm">
              <Stack gap="xs">
                <Text size="xs" fw={500}>
                  Recent Prompts
                </Text>
                {state.promptHistory.slice(-5).reverse().map((p, idx) => (
                  <Text
                    key={idx}
                    size="xs"
                    style={{ cursor: 'pointer' }}
                    onClick={() => setPrompt(p)}
                  >
                    • {p}
                  </Text>
                ))}
              </Stack>
            </Paper>
          </Collapse>

          {/* Suggestions List */}
          {state.pendingSuggestions.length > 0 && (
            <Stack gap="xs" style={{ flex: 1, minHeight: 0 }}>
              <Text size="sm" fw={500}>
                Suggestions ({state.pendingSuggestions.length})
              </Text>
              <ScrollArea style={{ flex: 1 }}>
                <Stack gap="xs">
                  {state.pendingSuggestions.map((suggestion) => (
                    <SuggestionCard
                      key={suggestion.suggestionId}
                      suggestion={suggestion}
                      isSelected={
                        state.selectedSuggestion?.suggestionId ===
                        suggestion.suggestionId
                      }
                      onSelect={() => selectSuggestion(suggestion)}
                      onApply={() => handleApply(suggestion.suggestionId)}
                      isApplying={state.isApplying}
                    />
                  ))}
                </Stack>
              </ScrollArea>
            </Stack>
          )}

          {/* Current Schema Preview */}
          {state.currentSchema && state.pendingSuggestions.length === 0 && (
            <Stack gap="xs" style={{ flex: 1, minHeight: 0 }}>
              <Group justify="space-between">
                <Text size="sm" fw={500}>
                  Current Schema
                </Text>
                <ActionIcon
                  variant="subtle"
                  size="sm"
                  onClick={() => {
                    navigator.clipboard.writeText(state.currentJson);
                    notifications.show({
                      title: 'Copied',
                      message: 'Schema JSON copied to clipboard',
                      color: 'green',
                    });
                  }}
                >
                  <IconClipboard size={14} />
                </ActionIcon>
              </Group>
              <ScrollArea style={{ flex: 1 }}>
                <Paper p="xs" bg="gray.0" radius="sm">
                  <Text
                    size="xs"
                    ff="monospace"
                    style={{ whiteSpace: 'pre-wrap' }}
                  >
                    {state.currentJson}
                  </Text>
                </Paper>
              </ScrollArea>
            </Stack>
          )}
        </Stack>
      )}
    </Stack>
  );
}

export default FormSchemaLlmPanel;
